// components/auth/ProtectedRoute.jsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, // Can be a single role string or array of roles
  requiredRoles = null, // Alternative: array of roles (for clarity)
  projectId = null,
  projectSlug = null,
  requireProjectAccess = false,
  fallbackPath = '/unauthorized',
  loginPath = '/login',
  unauthorizedPath = '/unauthorized',
  projectAccessDeniedPath = null,
  roleDeniedPath = null,
  preserveReturnUrl = true
}) => {
  const { user, loading, isAuthenticated, hasProjectAccess, hasRole, hasProjectRole } = useAuth();
  const location = useLocation();
  const [authChecked, setAuthChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [denialReason, setDenialReason] = useState(null);

useEffect(() => {
  // Only check access after loading is complete
  if (!loading) {
    checkAccess();
  }
}, [loading, user, projectId, projectSlug, requiredRole, requiredRoles]);

  const checkAccess = () => {
    if (!isAuthenticated) {
      setAuthChecked(true);
      setHasAccess(false);
      setDenialReason('unauthenticated');
      return;
    }

    const projectIdentifier = projectId || projectSlug;
    if (requireProjectAccess && projectIdentifier) {
      if (!hasProjectAccess(projectIdentifier)) {
        console.log(`User does not have access to project: ${projectIdentifier}`);
        setAuthChecked(true);
        setHasAccess(false);
        setDenialReason('project_access');
        return;
      }
    }

    // Handle role checking - support both single role and array of roles
    const rolesToCheck = requiredRoles || (requiredRole ? (Array.isArray(requiredRole) ? requiredRole : [requiredRole]) : null);
    
    if (rolesToCheck && rolesToCheck.length > 0) {
      let hasRequiredRole = false;
      
      if (projectIdentifier) {
        // Check if user has ANY of the required roles in this project
        hasRequiredRole = rolesToCheck.some(role => hasProjectRole(projectIdentifier, role));
        
        if (!hasRequiredRole) {
          console.log(`User does not have any of the required roles [${rolesToCheck.join(', ')}] in project ${projectIdentifier}`);
          setAuthChecked(true);
          setHasAccess(false);
          setDenialReason('role_access');
          return;
        }
      } else {
        // Check if user has ANY of the required global roles
        hasRequiredRole = rolesToCheck.some(role => hasRole(role));
        
        if (!hasRequiredRole) {
          console.log(`User does not have any of the required global roles: [${rolesToCheck.join(', ')}]`);
          setAuthChecked(true);
          setHasAccess(false);
          setDenialReason('role_access');
          return;
        }
      }
    }

    setAuthChecked(true);
    setHasAccess(true);
    setDenialReason(null);
  };

  const buildRedirectUrl = (basePath) => {
    if (!preserveReturnUrl) {
      return basePath;
    }
    const returnUrl = location.pathname + location.search;
    const separator = basePath.includes('?') ? '&' : '?';
    return `${basePath}${separator}returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  if (loading || !authChecked) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (denialReason === 'unauthenticated') {
    return <Navigate to={buildRedirectUrl(loginPath)} replace />;
  }

  if (!hasAccess) {
    const projectIdentifier = projectId || projectSlug;
    
    if (denialReason === 'project_access') {
      const redirectPath = projectAccessDeniedPath || 
        `/request-access?project=${projectIdentifier}`;
      return <Navigate to={buildRedirectUrl(redirectPath)} replace />;
    }
    
    if (denialReason === 'role_access') {
      const redirectPath = roleDeniedPath || unauthorizedPath || fallbackPath;
      return <Navigate to={buildRedirectUrl(redirectPath)} replace />;
    }
    
    return <Navigate to={buildRedirectUrl(unauthorizedPath || fallbackPath)} replace />;
  }

  return children;
};

// ============================================
// ROLE-SPECIFIC ROUTE COMPONENTS
// ============================================

// Admin Route - Highest level access
export const AdminRoute = ({ 
  children, 
  projectId = null,
  projectSlug = null,
  ...props 
}) => (
  <ProtectedRoute 
    requiredRole="admin"
    projectId={projectId}
    projectSlug={projectSlug}
    requireProjectAccess={projectId || projectSlug ? true : false}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// Owner Route - Project ownership level
export const OwnerRoute = ({ 
  children, 
  projectId = null,
  projectSlug = null,
  ...props 
}) => (
  <ProtectedRoute 
    requiredRole="owner"
    projectId={projectId}
    projectSlug={projectSlug}
    requireProjectAccess={projectId || projectSlug ? true : false}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// Uploader Route - Can upload content
export const UploaderRoute = ({ 
  children, 
  projectId = null,
  projectSlug = null,
  ...props 
}) => (
  <ProtectedRoute 
    requiredRole="uploader"
    projectId={projectId}
    projectSlug={projectSlug}
    requireProjectAccess={projectId || projectSlug ? true : false}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// Reviewer Route - Can review content
export const ReviewerRoute = ({ 
  children, 
  projectId = null,
  projectSlug = null,
  ...props 
}) => (
  <ProtectedRoute 
    requiredRole="reviewer"
    projectId={projectId}
    projectSlug={projectSlug}
    requireProjectAccess={projectId || projectSlug ? true : false}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// Viewer Route - Read-only access
export const ViewerRoute = ({ 
  children, 
  projectId = null,
  projectSlug = null,
  ...props 
}) => (
  <ProtectedRoute 
    requiredRole="viewer"
    projectId={projectId}
    projectSlug={projectSlug}
    requireProjectAccess={projectId || projectSlug ? true : false}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// User Route - Basic authenticated user
export const UserRoute = ({ 
  children, 
  projectId = null,
  projectSlug = null,
  ...props 
}) => (
  <ProtectedRoute 
    requiredRole="user"
    projectId={projectId}
    projectSlug={projectSlug}
    requireProjectAccess={projectId || projectSlug ? true : false}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// Project Route - Just requires project access (any role)
export const ProjectRoute = ({ 
  children, 
  projectId,
  projectSlug,
  ...props 
}) => (
  <ProtectedRoute 
    requireProjectAccess={true}
    projectId={projectId}
    projectSlug={projectSlug}
    {...props}
  >
    {children}
  </ProtectedRoute>
);

// Higher-order component version for class components
export const withProtectedRoute = (Component, options = {}) => {
  return (props) => (
    <ProtectedRoute {...options}>
      <Component {...props} />
    </ProtectedRoute>
  );
};

export default ProtectedRoute;